import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DecimalPipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { SmartInputComponent } from '@app/shared/smart-input/smart-input.component';
import { SearchProductComponent } from '@app/shared/search-product/search-product.component';
import { RentalBillingService, serverMessage } from '@app/sales/rental-billing/rental-billing.service';
import { todayStr } from '@app/shared/time';
import { RentalProductsService } from './rental-products.service';
import {
  BILLABLE_PERIODS, RentalCard, RentalProductRow, billingOf, firstPrice, gridAt, hasExplicitRules, secuFaults,
} from './rental-products.model';

type Tri = boolean | null;

// Une petite ligne de la liste : un contrat de location d'un article.
interface CardLine {
  card: RentalCard;
  periodLabel: string;
  price: number | null;
  lpp: boolean;
  lppCode: string;
  tiers: number;
  dated: boolean;
  sourceLabel: string;
  rulesLabel: string;
  rulesHint: string;
  secu: string[];
  explicit: boolean;
}

interface Group { row: RentalProductRow; lines: CardLine[]; hidden: number; }

const norm = (s: string | null | undefined) => (s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

@Component({
  selector: 'app-rental-products',
  templateUrl: './rental-products.component.html',
  styleUrls: ['./rental-products.component.scss'],
  imports: [FormsModule, RouterLink, DecimalPipe, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, MenuCommonComponent, SmartInputComponent, SearchProductComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class RentalProductsComponent implements OnInit {
  private service = inject(RentalProductsService);
  private router = inject(Router);
  private billing = inject(RentalBillingService);

  periodNames = new Map<number, string>();
  periods: { id: number; name: string }[] = [];
  readonly sources = [{ id: 'CARD', name: 'Prix du contrat' }, { id: 'LPP_TARIF', name: 'Tarif LPP' }, { id: 'LPP_PLV', name: 'PLV LPP' }];
  readonly TRI_HINT = 'Un clic fait tourner le filtre : Oui → Non → Tous.';

  rows: RentalProductRow[] = [];
  groups: Group[] = [];
  loading = false;
  error: string | null = null;
  cardCount = 0;
  sortKey: 'name' | 'id' = 'name';
  sortAsc = true;

  filter = { product: '', text: '', lpp: '', period: 0, source: '', secu: null as Tri, active: null as Tri, rules: null as Tri };

  ngOnInit(): void {
    this.billing.getPeriodNames().subscribe({
      next: (names) => {
        this.periodNames = names;
        this.periods = [...names].filter(([id]) => BILLABLE_PERIODS.includes(id)).map(([id, name]) => ({ id, name }));
        this.apply();
      },
    });
    this.load();
  }

  // Spécificité de l'écran : tout le catalogue arrive dès l'ouverture, les filtres travaillent ensuite sur place.
  load(): void {
    this.loading = true;
    this.error = null;
    this.service.getRentalProducts().subscribe({
      next: (rows) => { this.rows = rows ?? []; this.loading = false; this.apply(); },
      error: (e) => { this.loading = false; this.error = serverMessage(e); },
    });
  }

  apply(): void {
    const today = todayStr();
    const f = this.filter;
    const id = Number(f.product) || 0;
    const text = norm(f.text.trim());
    const lpp = f.lpp.trim();
    const groups: Group[] = [];
    let cards = 0;
    for (const row of this.rows) {
      if (id && row.id !== id) continue;
      const productHit = !text || norm(row.name).includes(text) || norm(row.partnbloc).includes(text) || norm(row.partnbsup).includes(text);
      const lines: CardLine[] = [];
      for (const card of row.cards) {
        if (text && !productHit && !norm(card.name).includes(text)) continue;
        const b = billingOf(card);
        const secu = secuFaults(b);
        if (lpp && !card.tiered_pricing.some((t) => (t.fse_lpp ?? '').startsWith(lpp))) continue;
        if (f.period && card.period !== f.period) continue;
        if (f.source && b.price_source !== f.source) continue;
        if (f.secu !== null && (secu.length === 0) !== f.secu) continue;
        if (f.active !== null && card.is_active !== f.active) continue;
        if (f.rules !== null && hasExplicitRules(card) !== f.rules) continue;
        lines.push(this.line(card, today));
      }
      if (!lines.length) continue;
      cards += lines.length;
      groups.push({ row, lines, hidden: row.cards.length - lines.length });
    }
    const dir = this.sortAsc ? 1 : -1;
    groups.sort((a, b) => dir * (this.sortKey === 'id' ? a.row.id - b.row.id : (a.row.name ?? '').localeCompare(b.row.name ?? '', 'fr')));
    this.groups = groups;
    this.cardCount = cards;
  }

  private line(card: RentalCard, today: string): CardLine {
    const b = billingOf(card);
    const grid = gridAt(card.tiered_pricing, today);
    const p = firstPrice(card, today);
    const rules = [
      b.interruption_rule === 'PAUSE' ? 'Pause' : b.interruption_rule === 'PRORATA' ? 'Prorata' : 'Entamée due',
      b.end_rule === 'PRORATA' ? 'fin prorata' : 'fin entière',
      b.overbilling === 'COMPENSATION' ? 'report' : 'avoir',
    ];
    if (b.prescription_required) rules.push('prescription');
    return {
      card,
      periodLabel: this.periodNames.get(card.period) ?? String(card.period),
      price: p.value, lpp: p.lpp,
      lppCode: grid.find((t) => t.starting_period === 1)?.fse_lpp ?? '',
      tiers: grid.length,
      dated: card.tiered_pricing.some((t) => !!t.valid_from),
      sourceLabel: b.price_source === 'CARD' ? 'Contrat' : b.price_source === 'LPP_TARIF' ? 'Tarif LPP' : 'PLV LPP',
      rulesLabel: rules.join(' · '),
      rulesHint: card.billing_error
        ? card.billing_error
        : hasExplicitRules(card) ? 'Règles posées sur le contrat' : 'Aucune règle posée : le moteur applique ses défauts',
      secu: secuFaults(b),
      explicit: hasExplicitRules(card),
    };
  }

  sortBy(key: 'name' | 'id'): void {
    this.sortAsc = this.sortKey === key ? !this.sortAsc : true;
    this.sortKey = key;
    this.apply();
  }

  cycle(key: 'secu' | 'active' | 'rules'): void {
    const v = this.filter[key];
    this.filter[key] = v === null ? true : v === true ? false : null;
    this.apply();
  }

  triIcon(v: Tri): string { return v === null ? 'indeterminate_check_box' : v ? 'check_box' : 'disabled_by_default'; }
  triText(v: Tri): string { return v === null ? 'Tous' : v ? 'Oui' : 'Non'; }

  clear(): void {
    this.filter = { product: '', text: '', lpp: '', period: 0, source: '', secu: null, active: null, rules: null };
    this.apply();
  }

  // Un produit choisi dans la recherche devient article de location : sa fiche s'ouvre, vide, prête pour son premier contrat de location.
  onProductPicked(p: any): void {
    const id = p?.ppv?.product ?? p?.id;
    if (id) this.router.navigate(['/sales/rental-product', id]);
  }

  secuHint(l: CardLine): string {
    return l.secu.length ? `Refusée en FSE par les contrôles Sécu :\n- ${l.secu.join('\n- ')}` : 'Passe les contrôles Sécu du passage (FSE)';
  }
}
